import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { map } from 'rxjs/operators';
import { ADMIN_NAV, DEFERRED_NAV_ITEMS } from '@aeCore/constants/nav-menu.const';
import { AuthService } from '@aeCore/services/auth.service';
import { SidebarLayoutService } from '@aeCore/services/sidebar-layout.service';
import { UserService } from '@aeCore/services/user.service';
import { UserModel } from '@aeCore/interfaces/user.interface';
import { isSuperAdminUser } from '@aeCore/utils/user-type.util';

interface SidebarUser {
  name: string;
  email: string;
  initial: string;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  readonly layout = inject(SidebarLayoutService);

  readonly authUser = toSignal(this.userService.currentUser$, {
    initialValue: null as UserModel | null,
  });

  readonly sections = computed(() => {
    const user = this.authUser();
    if (!user) return ADMIN_NAV;

    const permissions = new Set(user.permissions ?? []);
    const isSuperAdmin = isSuperAdminUser(user);

    return ADMIN_NAV.map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!item.permission?.length) return true;
        if (item.permission.some((p) => permissions.has(p))) return true;
        return isSuperAdmin;
      }),
    })).filter((section) => section.items.length > 0);
  });
  readonly deferredItems = DEFERRED_NAV_ITEMS;

  readonly currentUser = toSignal(
    this.userService.currentUser$.pipe(map((u) => this.toSidebarUser(u))),
    { initialValue: null as SidebarUser | null },
  );

  toggleCollapsed(): void {
    this.layout.toggle();
  }

  onLogout(): void {
    this.authService.logout();
  }

  private toSidebarUser(user: UserModel | null): SidebarUser | null {
    if (!user) return null;
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email;
    const initial = (user.firstName?.[0] ?? user.email?.[0] ?? '?').toUpperCase();
    return { name, email: user.email, initial };
  }
}
